import React, { useEffect, useMemo, useState } from 'react';
import plantillasContratoService from '../services/plantillasContratoService';

const ImportarPlantillasContratoModal = ({
  isOpen,
  onClose,
  empresaDestinoId,
  onSuccess,
}) => {
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiando, setCopiando] = useState(false);
  const [error, setError] = useState('');
  const [empresaOrigenId, setEmpresaOrigenId] = useState('');
  const [seleccionadas, setSeleccionadas] = useState([]);

  useEffect(() => {
    if (!isOpen || !empresaDestinoId) return;

    const cargar = async () => {
      setLoading(true);
      setError('');
      setSeleccionadas([]);
      setEmpresaOrigenId('');

      const response = await plantillasContratoService.obtenerCatalogoCopia(empresaDestinoId);
      if (response.success) {
        setCatalogo(response.data || []);
      } else {
        setCatalogo([]);
        setError(response.error || 'No se pudo cargar el catálogo');
      }

      setLoading(false);
    };

    cargar();
  }, [isOpen, empresaDestinoId]);

  const empresaSeleccionada = useMemo(
    () => catalogo.find((item) => String(item.empresa_id) === String(empresaOrigenId)),
    [catalogo, empresaOrigenId]
  );

  const plantillasDisponibles = empresaSeleccionada?.plantillas || [];

  const togglePlantilla = (id) => {
    setSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const seleccionarTodas = () => {
    const ids = plantillasDisponibles.map((item) => item.id);
    setSeleccionadas(ids);
  };

  const limpiarSeleccion = () => {
    setSeleccionadas([]);
  };

  const handleCopiar = async () => {
    if (!empresaDestinoId || seleccionadas.length === 0) return;

    setCopiando(true);
    setError('');

    const response = await plantillasContratoService.copiarPlantillas(empresaDestinoId, seleccionadas);

    if (response.success) {
      onSuccess?.(response.message);
      onClose();
    } else {
      setError(response.error || 'No se pudieron copiar las plantillas');
    }

    setCopiando(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Importar plantillas de otra empresa</h3>
          <p className="text-sm text-gray-600 mt-1">
            Copia modelos de contrato ya creados en otras empresas del sistema hacia la empresa actual.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500">Cargando plantillas disponibles...</p>
          ) : catalogo.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              No hay plantillas en otras empresas para importar. Crea plantillas en otra empresa primero.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Empresa origen</label>
                <select
                  value={empresaOrigenId}
                  onChange={(e) => {
                    setEmpresaOrigenId(e.target.value);
                    setSeleccionadas([]);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleccionar empresa...</option>
                  {catalogo.map((empresa) => (
                    <option key={empresa.empresa_id} value={empresa.empresa_id}>
                      {empresa.empresa_nombre}
                      {empresa.empresa_ruc ? ` (RUC ${empresa.empresa_ruc})` : ''}
                      {' — '}
                      {empresa.plantillas?.length || 0} plantilla(s)
                    </option>
                  ))}
                </select>
              </div>

              {empresaSeleccionada && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-gray-700">
                      Plantillas de {empresaSeleccionada.empresa_nombre}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={seleccionarTodas}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        Seleccionar todas
                      </button>
                      <button
                        type="button"
                        onClick={limpiarSeleccion}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>

                  {plantillasDisponibles.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">Esta empresa no tiene plantillas.</p>
                  ) : (
                    <div className="divide-y divide-gray-200 max-h-72 overflow-y-auto">
                      {plantillasDisponibles.map((plantilla) => (
                        <label
                          key={plantilla.id}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={seleccionadas.includes(plantilla.id)}
                            onChange={() => togglePlantilla(plantilla.id)}
                            className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{plantilla.titulo}</p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {plantilla.tipo} · {plantilla.articulos_count} artículo(s)
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={copiando}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCopiar}
            disabled={copiando || seleccionadas.length === 0}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {copiando
              ? 'Importando...'
              : seleccionadas.length > 0
                ? `Importar ${seleccionadas.length} plantilla(s)`
                : 'Importar seleccionadas'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportarPlantillasContratoModal;
